import { LogLevel, type Configuration } from '@azure/msal-browser';
import type { AppConfig } from '@/config/appConfig';

/**
 * Builds the MSAL PublicClientApplication configuration from the app config.
 * Both redirectUri and postLogoutRedirectUri are fixed to '/' (the application
 * root). MSAL resolves this against window.location.origin at runtime, so the
 * full URL (e.g. http://localhost:3220/) must be registered as a SPA redirect
 * URI in the Azure app registration for every environment.
 * Cache is stored in localStorage for persistence across page refreshes and tabs.
 */
export function buildMsalConfig(config: AppConfig): Configuration {
    return {
        auth: {
            clientId: config.clientId,
            authority: config.appAuthority,
            redirectUri: '/',
            postLogoutRedirectUri: '/',
        },
        cache: {
            cacheLocation: 'localStorage',
        },
        system: {
            loggerOptions: {
                logLevel: LogLevel.Warning,
                loggerCallback: (level, message, containsPii) => {
                    // Never log messages that may contain personally identifiable information
                    if (containsPii) return;
                    switch (level) {
                        case LogLevel.Error:
                            console.error('[MSAL]', message);
                            break;
                        case LogLevel.Warning:
                            console.warn('[MSAL]', message);
                            break;
                        default:
                            // Suppress Info/Verbose in production to keep the console clean
                            break;
                    }
                },
            },
        },
    };
}

/**
 * Builds a login redirect request using the configured API scopes.
 * Scopes must match those registered on the Azure app registration.
 */
export function buildLoginRequest(config: AppConfig) {
    return { scopes: config.apiScopes };
}

/**
 * Builds a silent token request for the configured API scopes.
 */
export function buildTokenRequest(config: AppConfig) {
    return { scopes: config.apiScopes };
}
